import {
  DEAD_VIEW_DIST_BONUS,
  DEFAULT_VIEW_DIST,
  LERP,
  uiScaleFor,
  VIEW_DIST_PER_SHIP,
} from '../game/constants/feel';
import { viewportScale } from '../game/constants/ships';
import type { ServerMessage } from '../protocol/gen/warinspace/v2/server_to_client_pb.js';

export class Camera {
  /** Server-driven only: the spawn packet and a ship change set this; nothing else (stat upgrades included) touches it, and there is no client zoom control. */
  private baseViewDist = DEFAULT_VIEW_DIST;

  dead = false;

  /** Set only while spectating. A player's view distance stays server-driven, so this must be back to null before a spectator takes a seat. */
  freeViewDist: number | null = null;

  get viewDist(): number {
    if (this.freeViewDist !== null) return this.freeViewDist;
    return this.baseViewDist + (this.dead ? DEAD_VIEW_DIST_BONUS : 0);
  }

  worldScale = 1;

  centreX = 0;
  centreY = 0;

  private viewportWidth = 1;
  private viewportHeight = 1;
  private ratio = 1;

  get deviceWidth(): number {
    return this.viewportWidth;
  }

  get deviceHeight(): number {
    return this.viewportHeight;
  }

  get pixelRatio(): number {
    return this.ratio;
  }

  resize(width: number, height: number, ratio = 1): void {
    this.viewportWidth = Math.max(1, width);
    this.viewportHeight = Math.max(1, height);
    this.ratio = ratio > 0 ? ratio : 1;
  }

  get targetScale(): number {
    return Math.max(this.viewportWidth, this.viewportHeight) / this.viewDist;
  }

  get uiScale(): number {
    return uiScaleFor(this.worldScale);
  }

  get worldWidth(): number {
    return this.viewportWidth / this.worldScale;
  }

  get worldHeight(): number {
    return this.viewportHeight / this.worldScale;
  }

  get uiWidth(): number {
    return this.viewportWidth / this.uiScale;
  }

  get uiHeight(): number {
    return this.viewportHeight / this.uiScale;
  }

  update(): void {
    const target = this.targetScale;
    if (this.worldScale === 0) {
      this.worldScale = target;
      return;
    }
    this.worldScale += (target - this.worldScale) * LERP.cameraZoom;
  }

  snap(): void {
    this.worldScale = this.targetScale;
  }

  setViewDistForShip(shipId: number): void {
    this.baseViewDist = (VIEW_DIST_PER_SHIP * (viewportScale(shipId) + 3)) / 4;
  }

  get offsetX(): number {
    return this.centreX - this.worldWidth / 2;
  }

  get offsetY(): number {
    return this.centreY - this.worldHeight / 2;
  }

  worldToScreen(x: number, y: number): { x: number; y: number } {
    return {
      x: (x - this.offsetX) * this.worldScale,
      y: (y - this.offsetY) * this.worldScale,
    };
  }

  isVisible(x: number, y: number, radius: number): boolean {
    return (
      x + radius >= this.offsetX &&
      x - radius <= this.offsetX + this.worldWidth &&
      y + radius >= this.offsetY &&
      y - radius <= this.offsetY + this.worldHeight
    );
  }
}

/** Uses the ship id the client already holds, not a hard-coded default, so a respawn (which keeps the pre-death ship) doesn't snap the camera back to base zoom. */
export function applyCameraPacket(camera: Camera, message: ServerMessage, shipId: number): void {
  const payload = message.payload;
  if (payload.case === 'init') {
    camera.setViewDistForShip(shipId);
    camera.snap();
  } else if (payload.case === 'shipChanged') {
    camera.setViewDistForShip(payload.value.shipId);
  }
}
